import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '../../api/http';
import { workspacesApi } from '../../api/workspaces';
import { useAuthStore } from '../../stores/useAuthStore';

/**
 * 邀請連結的落地頁。
 *
 * 受邀者點連結時**還沒登入**，所以這一頁必須在未登入狀態也顯示得出「誰邀我、加入哪裡」
 * ——什麼都不說就把人導去登入頁，那個人根本不知道自己在登入什麼。
 */
export function AcceptInvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const authStatus = useAuthStore((state) => state.status);
  const refreshSession = useAuthStore((state) => state.refreshSession);

  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => workspacesApi.previewInvitation(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => workspacesApi.acceptInvitation(token),
    onSuccess: async () => {
      // 加入之後要換一張帶著新 workspace 的 token，不然畫面還停在原本那個
      await refreshSession();
      navigate('/');
    },
  });

  if (preview.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-4 text-muted-foreground" />
      </div>
    );
  }

  if (preview.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {preview.error instanceof ApiError ? preview.error.message : t('invitation_invalid')}
        </AlertDescription>
      </Alert>
    );
  }

  const status = preview.data.status;

  return (
    <div className="flex max-w-120 flex-col gap-4 rounded-xl bg-card p-6 shadow-card ring-1 ring-foreground/10">
      <h3 className="text-lg font-bold tracking-tight">
        {t('invitation_title', { workspace: preview.data.workspaceName })}
      </h3>
      <p className="text-sm text-muted-foreground">
        {t('invitation_for', { email: preview.data.email, role: t(`role_${preview.data.role}`) })}
      </p>

      {status !== 'pending' && (
        <Alert className="border-warning/40 bg-warning/10 text-warning">
          <AlertDescription className="text-warning">
            {t(`invitation_status_${status}`)}
          </AlertDescription>
        </Alert>
      )}

      {status === 'pending' && authStatus !== 'authenticated' && (
        <div className="flex flex-col gap-2">
          <p className="text-sm">{t('invitation_need_account')}</p>
          <Button
            render={
              <Link
                to={`/register?invitation=${token}&email=${encodeURIComponent(preview.data.email)}`}
              />
            }
          >
            {t('invitation_register')}
          </Button>
          <Link to="/login" className={buttonVariants({ variant: 'ghost' })}>
            {t('invitation_login')}
          </Link>
        </div>
      )}

      {status === 'pending' && authStatus === 'authenticated' && (
        <div className="flex flex-col gap-2">
          {accept.isError && (
            <Alert variant="destructive">
              <AlertDescription>
                {accept.error instanceof ApiError ? accept.error.message : t('auth_error_unknown')}
              </AlertDescription>
            </Alert>
          )}
          <Button disabled={accept.isPending} onClick={() => accept.mutate()}>
            {accept.isPending && <Spinner data-icon="inline-start" />}
            {t('invitation_accept')}
          </Button>
        </div>
      )}
    </div>
  );
}
